import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import updatePlatformNumber from '@/API/PlatformNumber/updatePlatformNumber';
import deallocatePlatformNumbersTrunk from '@/API/PlatformNumber/deallocatePlatformNumbersTrunk';
import { PlatformEnums } from '@/utils/platforms';
import { TEAMS_BULK_NUMBERS_KEY } from '../constants';
import { buildPayload } from '../utils/bulkUpdate';

// Other updates have no bulk endpoint, so numbers are updated one request
// each, a few at a time.
export const CONCURRENCY = 4;

// Trunk removals go to DeallocateTrunk in batches, one batch at a time, so
// progress and Cancel still work on long runs.
export const DEALLOCATE_BATCH_SIZE = 100;

/** A number that has a trunk and ends up with none. */
export const isTrunkRemoval = (item) =>
    !!item.row.trunkID && !item.next.trunkID;

const toBatches = (items, size) =>
    Array.from({ length: Math.ceil(items.length / size) }, (_, index) =>
        items.slice(index * size, (index + 1) * size),
    );

const FALLBACK_REASON = 'The update could not be completed';

export const IDLE_RUN = {
    status: 'idle',
    total: 0,
    processed: 0,
    success: 0,
    ignored: 0,
    failed: [],
    // Cancel was pressed: updates already sent are finishing
    isCancelling: false,
    // Numbers a cancelled run never sent (left unchanged)
    notSent: 0,
};

export const getFailureReason = (error) =>
    error?.response?.data?.message || error?.message || FALLBACK_REASON;

/**
 * Runs `worker` over `items` with at most `limit` in flight at once. Once
 * `shouldContinue` returns false no more items are started; ones already in
 * flight still finish.
 */
export const runWithConcurrency = async (
    items,
    limit,
    worker,
    shouldContinue = () => true,
) => {
    let nextIndex = 0;
    const lane = async () => {
        while (nextIndex < items.length && shouldContinue()) {
            const item = items[nextIndex];
            nextIndex += 1;
            await worker(item);
        }
    };
    await Promise.all(
        Array.from({ length: Math.min(limit, items.length) }, lane),
    );
};

/**
 * Applies a Bulk Update plan. Trunk removals are sent to
 * `POST /PlatformNumbers/Process/{PlatformID}/DeallocateTrunk` in batches; every
 * other changing number gets its own `PUT /PlatformNumber`. A failed batch
 * fails all of its numbers with the server's reason.
 * Numbers with nothing to change are counted as ignored. Successful PUTs are
 * written into the cached numbers list so the new values show for the rest of
 * the session; failed numbers are left as they were. After any trunk removal
 * the list is refetched instead, as DeallocateTrunk only queues the change.
 *
 * `cancel` stops the run early: updates already sent finish, the rest are
 * never sent and counted as `notSent`. Leaving the page cancels it too (a
 * refresh does the same, as the requests live in this page).
 */
const useBulkUpdateRunner = ({ scope }) => {
    const queryClient = useQueryClient();
    const [run, setRun] = useState(IDLE_RUN);
    const isMountedRef = useRef(true);
    const isCancelRequestedRef = useRef(false);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const start = useCallback(
        async (items) => {
            const toUpdate = items.filter((item) => item.diff.length > 0);
            const ignored = items.length - toUpdate.length;
            const updated = new Map();
            let hasSentDeallocation = false;
            const updateRun = (change) => {
                if (isMountedRef.current) setRun(change);
            };
            isCancelRequestedRef.current = false;

            setRun({
                ...IDLE_RUN,
                status: 'running',
                total: items.length,
                processed: ignored,
                ignored,
            });

            const recordSuccess = (count) =>
                updateRun((current) => ({
                    ...current,
                    processed: current.processed + count,
                    success: current.success + count,
                }));
            const recordFailure = (failedItems, error) => {
                const reason = getFailureReason(error);
                updateRun((current) => ({
                    ...current,
                    processed: current.processed + failedItems.length,
                    failed: [
                        ...current.failed,
                        ...failedItems.map((item) => ({
                            id: item.row.id,
                            e164: item.row.e164,
                            reason,
                        })),
                    ],
                }));
            };
            const shouldContinue = () =>
                isMountedRef.current && !isCancelRequestedRef.current;

            const deallocateBatch = async (batch) => {
                hasSentDeallocation = true;
                try {
                    await deallocatePlatformNumbersTrunk({
                        platformId: PlatformEnums.TEAMS,
                        numberIds: batch.map((item) => item.row.id),
                        scope,
                    });
                    recordSuccess(batch.length);
                } catch (error) {
                    recordFailure(batch, error);
                }
            };
            const updateNumber = async (item) => {
                const payload = buildPayload(item.row, item.next);
                try {
                    const response = await updatePlatformNumber({
                        formData: payload,
                        scope,
                    });
                    updated.set(item.row.id, {
                        ...payload,
                        ...(response && typeof response === 'object'
                            ? response
                            : {}),
                    });
                    recordSuccess(1);
                } catch (error) {
                    recordFailure([item], error);
                }
            };

            await runWithConcurrency(
                toBatches(
                    toUpdate.filter(isTrunkRemoval),
                    DEALLOCATE_BATCH_SIZE,
                ),
                1,
                deallocateBatch,
                shouldContinue,
            );
            await runWithConcurrency(
                toUpdate.filter((item) => !isTrunkRemoval(item)),
                CONCURRENCY,
                updateNumber,
                shouldContinue,
            );

            // One cache write at the end, so the lists re-render once. Done
            // even after leaving the page, as those numbers did change.
            if (updated.size > 0) {
                queryClient.setQueriesData(
                    { queryKey: [TEAMS_BULK_NUMBERS_KEY] },
                    (data) =>
                        data?.platformNumbers
                            ? {
                                  ...data,
                                  platformNumbers: data.platformNumbers.map(
                                      (number) =>
                                          updated.has(number.numberID)
                                              ? {
                                                    ...number,
                                                    ...updated.get(
                                                        number.numberID,
                                                    ),
                                                }
                                              : number,
                                  ),
                              }
                            : data,
                );
            }
            // DeallocateTrunk leaves numbers on their trunk, Processing, until
            // the Teams sync runs, so show what the server now holds
            if (hasSentDeallocation) {
                queryClient.invalidateQueries({
                    queryKey: [TEAMS_BULK_NUMBERS_KEY],
                });
            }

            updateRun((current) => ({
                ...current,
                status: 'done',
                isCancelling: false,
                notSent: current.total - current.processed,
            }));
        },
        [queryClient, scope],
    );

    const reset = useCallback(() => setRun(IDLE_RUN), []);

    const cancel = useCallback(() => {
        isCancelRequestedRef.current = true;
        setRun((current) =>
            current.status === 'running'
                ? { ...current, isCancelling: true }
                : current,
        );
    }, []);

    return { run, start, cancel, reset };
};

export default useBulkUpdateRunner;
